/**
 * ASK-KNOW-01 — Library notes: write, read and revise the short answers the
 * office wants everyone (and Ask Pegasus) to know. Used by Team → Library
 * and Settings → Ask Pegasus ("Teach it").
 */
import { useEffect, useState } from "react";
import { StickyNote } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { useLibraryNote, useSaveLibraryNote } from "./team";

export const NOTES_FOLDER = "Notes";

const errText = (e: unknown) =>
  e instanceof Error ? e.message : typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : String(e);

const fmt = (ts: string | null | undefined) =>
  ts ? new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

export function NoteEditorModal({
  open, onClose, noteId = null, initialTitle = "", initialFolder = NOTES_FOLDER, folders, hint, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** Editing an existing note; null = a new one. */
  noteId?: string | null;
  initialTitle?: string;
  initialFolder?: string;
  folders: string[];
  /** A line of context above the form (e.g. the answer that was wrong). */
  hint?: React.ReactNode;
  onSaved?: (id: string) => void;
}) {
  const existing = useLibraryNote(open ? noteId : null).data ?? null;
  const save = useSaveLibraryNote();
  const [title, setTitle] = useState(initialTitle);
  const [folder, setFolder] = useState(initialFolder);
  const [body, setBody] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    if (noteId && existing) {
      setTitle(existing.filename); setFolder(existing.folder); setBody(existing.content_text ?? "");
    } else if (!noteId) {
      setTitle(initialTitle); setFolder(initialFolder); setBody("");
    }
  }, [open, noteId, existing, initialTitle, initialFolder]);

  const choices = [...new Set([NOTES_FOLDER, ...folders, folder].filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const ready = title.trim().length >= 3 && body.trim().length >= 2;

  const submit = () => {
    if (!ready) return;
    setErr(null);
    save.mutate({ id: noteId, title, folder, body }, {
      onSuccess: (id) => { onSaved?.(id); onClose(); },
      onError: (e) => setErr(errText(e)),
    });
  };

  return (
    <Modal open={open} onClose={onClose} size="lg"
      icon={<StickyNote className="h-5 w-5 text-amber-600" />}
      title={
        <span className="flex items-center gap-2">
          {noteId ? "Edit Note" : "New Note"}
          <InfoTip label="About notes">A short answer everyone can find. The Library, the phones and Ask Pegasus all read it, and Ask Pegasus trusts a note over older documents.</InfoTip>
        </span>
      }
      footer={
        <div className="flex w-full items-center gap-2">
          {err && <span className="text-xs text-rose-600">Couldn't save — {err}</span>}
          <span className="ml-auto" />
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" loading={save.isPending} disabled={!ready} onClick={submit}>Save Note</Button>
        </div>
      }>
      <div className="space-y-3">
        {hint && <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600">{hint}</div>}
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Title — usually the question people ask</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus placeholder="e.g. Who is our general contractor for repair work?"
            className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm focus:border-brand-500 focus:outline-none" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">The answer</span>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7}
            placeholder="e.g. Upright Home Services LLC — Kevin Clark, 952-234-0284, rkevin.clark@comcast.net. We send them repair and remodel work we don't do."
            className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm leading-relaxed focus:border-brand-500 focus:outline-none" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Category</span>
          <select value={folder} onChange={(e) => setFolder(e.target.value)}
            className="mt-1 block w-full max-w-xs rounded-lg border border-hairline px-3 py-2 text-sm focus:border-brand-500 focus:outline-none">
            {choices.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>
        {existing?.updated_at && (
          <p className="text-[11px] text-slate-400">Last changed {fmt(existing.updated_at)}{existing.updated_by ? ` by ${existing.updated_by}` : ""} — earlier wording stays in the Audit Log.</p>
        )}
      </div>
    </Modal>
  );
}

export function NoteViewerModal({ noteId, onClose, onEdit }: { noteId: string | null; onClose: () => void; onEdit?: () => void }) {
  const note = useLibraryNote(noteId).data ?? null;
  return (
    <Modal open={!!noteId} onClose={onClose} size="lg"
      icon={<StickyNote className="h-5 w-5 text-amber-600" />}
      title={note?.filename ?? "Note"}
      subtitle={note ? `${note.folder}${note.updated_at ? ` · updated ${fmt(note.updated_at)}${note.updated_by ? ` by ${note.updated_by}` : ""}` : ""}` : undefined}
      footer={
        <div className="flex w-full items-center gap-2">
          <span className="ml-auto" />
          {onEdit && <Button variant="secondary" size="sm" onClick={onEdit}>Edit</Button>}
          <Button size="sm" onClick={onClose}>Done</Button>
        </div>
      }>
      {note ? (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{note.content_text}</p>
      ) : (
        <div className="h-24 animate-pulse rounded-lg bg-slate-100" />
      )}
    </Modal>
  );
}
